/**
 * Phase X.2 — Audiences + Assets tabs + AI Campaign Generator + Cron Scheduler.
 * Mounted from MarketingOS.jsx.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Users, Sparkles, X, FileText, Folder, Clock, Calendar } from "lucide-react";

export function AudiencesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAI, setShowAI] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [newName, setNewName] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/marketing/audiences");
      setRows(r.data.rows || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runAI = async () => {
    if (!aiPrompt.trim()) return;
    setAiBusy(true);
    try {
      const r = await api.post("/admin/marketing/ai/build-audience", { prompt: aiPrompt });
      setAiResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI builder failed");
    } finally { setAiBusy(false); }
  };

  const saveAudience = async () => {
    if (!newName.trim() || !aiResult?.filter) { toast.error("Name + AI filter required"); return; }
    try {
      await api.post("/admin/marketing/audiences", {
        name: newName, filters: aiResult.filter, description: aiPrompt,
      });
      toast.success("Audience saved");
      setShowSave(false); setShowAI(false); setAiResult(null); setNewName(""); setAiPrompt("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (a) => {
    if (a.preset) { toast.error("Cannot delete a preset audience"); return; }
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try { await api.delete(`/admin/marketing/audiences/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="mk-audiences">
      <div className="flex justify-end gap-2">
        <button onClick={() => setShowAI(true)} data-testid="mk-aud-ai-btn"
          className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 border border-purple-300 text-purple-800 text-xs font-bold rounded-lg inline-flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> AI Audience Builder
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {loading && <div className="col-span-full text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
        {rows.map(a => (
          <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-3" data-testid={`mk-aud-${a.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-bold text-[#1a3c5e] text-sm flex items-center gap-1">
                <Users className="w-3.5 h-3.5 text-blue-600" /> {a.name}
                {a.preset && <span className="text-[9px] uppercase font-bold text-amber-700 ml-1">preset</span>}
              </div>
              {!a.preset && (
                <button onClick={() => del(a)} className="text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
            {a.description && <div className="text-[11px] text-gray-500 italic mb-1.5">{a.description}</div>}
            <div className="text-[10px] text-gray-500 mb-1">Filters: {JSON.stringify(a.filters).slice(0, 80)}</div>
            <div className="text-xs font-bold text-emerald-700 tabular-nums">{a.matched ?? 0} matched leads</div>
          </div>
        ))}
      </div>

      {showAI && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mk-aud-ai-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" /> AI Audience Builder</h3>
              <button onClick={() => { setShowAI(false); setAiResult(null); }}><X className="w-4 h-4" /></button>
            </div>
            <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} rows={3}
              placeholder='e.g. "Send to hot leads in Mumbai who asked about 3BHK price"'
              data-testid="mk-aud-ai-prompt"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <button onClick={runAI} disabled={aiBusy || !aiPrompt.trim()} data-testid="mk-aud-ai-run"
              className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
              {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Build
            </button>
            {aiResult && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-1 text-xs" data-testid="mk-aud-ai-result">
                <div className="text-[10px] uppercase font-bold text-purple-700">AI filter</div>
                <pre className="text-[11px] bg-white p-2 rounded overflow-auto">{JSON.stringify(aiResult.filter, null, 2)}</pre>
                <div className="text-emerald-700 font-bold">{aiResult.matched} matched leads</div>
                {aiResult.explanation && <div className="text-gray-600 italic">{aiResult.explanation}</div>}
                <div className="flex justify-end pt-1">
                  <button onClick={() => setShowSave(true)} className="px-3 py-1 bg-[#1a3c5e] hover:bg-[#0f2940] text-white text-xs font-bold rounded inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Save as audience
                  </button>
                </div>
              </div>
            )}
            {showSave && (
              <div className="border-t pt-3 space-y-2">
                <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Audience name"
                  className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setShowSave(false)} className="px-3 py-1 text-xs font-bold border border-gray-200 rounded">Cancel</button>
                  <button onClick={saveAudience} data-testid="mk-aud-save-btn" className="px-3 py-1 bg-[#1a3c5e] text-white text-xs font-bold rounded">Save</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function AssetsTab() {
  const [rows, setRows] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [n, setN] = useState({ title: "", kind: "portfolio", file_url: "", folder: "", tags: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (folder) params.set("folder", folder);
      if (q) params.set("q", q);
      const r = await api.get(`/admin/marketing/assets?${params.toString()}`);
      setRows(r.data.rows || []);
      setFolders(r.data.folders || []);
    } finally { setLoading(false); }
  }, [folder, q]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!n.title || !n.file_url) { toast.error("Title + URL required"); return; }
    try {
      await api.post("/admin/marketing/assets", {
        title: n.title, kind: n.kind, file_url: n.file_url,
        folder: n.folder || "general",
        tags: n.tags.split(",").map(x => x.trim()).filter(Boolean),
      });
      toast.success("Asset added");
      setShowAdd(false); setN({ title: "", kind: "portfolio", file_url: "", folder: "", tags: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try { await api.delete(`/admin/marketing/assets/${a.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-3" data-testid="mk-assets">
      <div className="flex items-center gap-2 flex-wrap">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title/tag…"
          className="flex-1 min-w-[200px] px-2 py-1.5 border border-gray-200 rounded text-xs" data-testid="mk-assets-search" />
        <select value={folder} onChange={e => setFolder(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded text-xs">
          <option value="">All folders</option>
          {folders.map(f => <option key={f} value={f}>{f}</option>)}
        </select>
        <button onClick={() => setShowAdd(true)} data-testid="mk-asset-add-btn"
          className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> Add asset
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {loading && <div className="col-span-full text-center py-10 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
        {!loading && rows.length === 0 && <div className="col-span-full text-center py-10 text-sm text-gray-500 italic">No assets yet.</div>}
        {rows.map(a => (
          <div key={a.id} className="bg-white border border-gray-200 rounded-xl p-3" data-testid={`mk-asset-${a.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-bold text-[#1a3c5e] text-sm flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-orange-600" /> {a.title}
              </div>
              <button onClick={() => del(a)} className="text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="text-[10px] text-gray-500"><Folder className="w-3 h-3 inline mr-1" />{a.folder} · {a.kind}</div>
            <div className="flex flex-wrap gap-1 mt-1">
              {(a.tags || []).map(t => <span key={t} className="px-1.5 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-orange-700 text-[10px]">{t}</span>)}
            </div>
            <a href={a.file_url} target="_blank" rel="noreferrer" className="text-[11px] text-blue-600 hover:underline mt-1 inline-block break-all">{a.file_url}</a>
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mk-asset-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2"><Plus className="w-4 h-4" /> Add asset</h3>
              <button onClick={() => setShowAdd(false)}><X className="w-4 h-4" /></button>
            </div>
            <input value={n.title} onChange={e => setN({ ...n, title: e.target.value })} placeholder="Title *" data-testid="mk-asset-title"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
            <input value={n.file_url} onChange={e => setN({ ...n, file_url: e.target.value })} placeholder="File URL * (https://…)" data-testid="mk-asset-url"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <select value={n.kind} onChange={e => setN({ ...n, kind: e.target.value })} className="px-2 py-1.5 border border-gray-200 rounded text-xs">
                {["portfolio","brochure","catalogue","offer","image","video","pdf","testimonial","before_after","other"].map(k => <option key={k}>{k}</option>)}
              </select>
              <input value={n.folder} onChange={e => setN({ ...n, folder: e.target.value })} placeholder="Folder (default: general)"
                className="px-2 py-1.5 border border-gray-200 rounded text-xs" />
            </div>
            <input value={n.tags} onChange={e => setN({ ...n, tags: e.target.value })} placeholder="Tags (comma-separated)"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded">Cancel</button>
              <button onClick={add} data-testid="mk-asset-save" className="px-3 py-1.5 bg-[#1a3c5e] text-white text-xs font-bold rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ====================== AI CAMPAIGN GENERATOR ====================== */

const CRON_PRESETS = [
  { label: "Mondays 10:00 UTC", cron: "0 10 * * 1" },
  { label: "Every weekday 09:00 UTC", cron: "0 9 * * 1-5" },
  { label: "1st of month 09:00 UTC", cron: "0 9 1 * *" },
  { label: "Every Sunday 18:00 UTC", cron: "0 18 * * 0" },
  { label: "Daily 12:00 UTC", cron: "0 12 * * *" },
];

export function AICampaignGenerator({ onClose, onApply }) {
  const [prompt, setPrompt] = useState("");
  const [objective, setObjective] = useState("increase_orders");
  const [language, setLanguage] = useState("en_US");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/marketing/ai/generate-campaign", {
        prompt, objective, language,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generator failed");
    } finally { setBusy(false); }
  };

  const apply = () => {
    if (!result?.campaign) return;
    onApply?.(result.campaign);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mk-ai-gen-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-5 space-y-3 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" /> AI Campaign Generator
          </h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} rows={3}
          placeholder='e.g. "Diwali promo for 3BHK package, target hot leads in Mumbai"'
          data-testid="mk-ai-gen-prompt"
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <select value={objective} onChange={e => setObjective(e.target.value)} data-testid="mk-ai-gen-objective"
            className="px-2 py-1.5 border border-gray-200 rounded text-xs">
            {["increase_orders","book_consultation","generate_quotation","collect_requirements",
              "upsell","cross_sell","recover_leads","collect_reviews","generate_referrals"]
              .map(o => <option key={o} value={o}>{o.replaceAll("_", " ")}</option>)}
          </select>
          <select value={language} onChange={e => setLanguage(e.target.value)}
            className="px-2 py-1.5 border border-gray-200 rounded text-xs">
            {["en_US","en_GB","hi","mr","gu","ta","te","bn","pa"].map(l => <option key={l}>{l}</option>)}
          </select>
        </div>
        <button onClick={run} disabled={busy || !prompt.trim()} data-testid="mk-ai-gen-run"
          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Generate with AI
        </button>

        {result?.campaign && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 space-y-2 text-xs" data-testid="mk-ai-gen-result">
            <div className="font-bold text-[#1a3c5e] text-sm">{result.campaign.campaign_name}</div>
            <div className="text-purple-800 font-bold italic">{result.campaign.headline}</div>
            <div className="bg-white border border-purple-200 rounded p-2 whitespace-pre-wrap text-gray-700">{result.campaign.body}</div>
            {result.campaign.footer && <div className="text-[10px] text-gray-500">{result.campaign.footer}</div>}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-600">Recommended CTA</div>
                <div className="text-gray-700">{result.campaign.cta?.primary || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-600">Best send time</div>
                <div className="text-gray-700">{result.campaign.recommended_send_time || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-600">Est CTR</div>
                <div className="text-emerald-700 font-bold">{result.campaign.estimated_ctr_percent}%</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-600">Est conversion</div>
                <div className="text-emerald-700 font-bold">{result.campaign.estimated_conversion_percent}%</div>
              </div>
            </div>
            {result.campaign.ab_variant && (
              <details className="border-t pt-2">
                <summary className="text-[10px] uppercase font-bold text-gray-600 cursor-pointer">A/B variant</summary>
                <div className="mt-1 text-gray-700 italic">{result.campaign.ab_variant.headline}</div>
                <div className="text-gray-600 whitespace-pre-wrap">{result.campaign.ab_variant.body}</div>
              </details>
            )}
            {result.knowledge_used?.length > 0 && (
              <details className="border-t pt-2">
                <summary className="text-[10px] uppercase font-bold text-gray-600 cursor-pointer">Knowledge sources ({result.knowledge_used.length})</summary>
                <ul className="mt-1 space-y-1">
                  {result.knowledge_used.map((c, i) => (
                    <li key={i} className="text-[10px] text-gray-500 truncate">
                      [{i + 1}] {c.title || c.url || c.content?.slice(0, 80)}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button onClick={() => setResult(null)} className="px-3 py-1 text-xs font-bold border border-gray-200 rounded">Discard</button>
              <button onClick={apply} data-testid="mk-ai-gen-apply"
                className="px-3 py-1 bg-[#1a3c5e] text-white text-xs font-bold rounded inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> Use this draft
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ====================== CRON SCHEDULE MODAL ====================== */

export function ScheduleCampaignModal({ campaign, onClose, onSaved }) {
  const [cron, setCron] = useState(campaign?.schedule?.cron || "");
  const [tz, setTz] = useState(campaign?.schedule?.timezone || "UTC");
  const [runs, setRuns] = useState(campaign?.schedule?.runs || "");
  const [startsAt, setStartsAt] = useState(
    campaign?.schedule?.starts_at ? campaign.schedule.starts_at.slice(0, 16) : ""
  );
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (cron) {
      const parts = cron.trim().split(/\s+/);
      if (parts.length !== 5) {
        toast.error("Cron must be 5 fields: m h dom mon dow");
        return;
      }
    }
    setSaving(true);
    try {
      await api.post(`/admin/marketing/campaigns/${campaign.id}/schedule`, {
        cron: cron || null,
        timezone: tz,
        runs: runs ? Number(runs) : null,
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
      });
      toast.success("Schedule saved");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="mk-schedule-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#1a3c5e] flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" /> Schedule recurrence
          </h3>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="text-xs text-gray-600">
          Campaign: <span className="font-bold text-[#1a3c5e]">{campaign?.name}</span>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] uppercase font-bold text-gray-600">Quick presets</label>
          <div className="flex flex-wrap gap-1">
            {CRON_PRESETS.map(p => (
              <button key={p.cron} onClick={() => setCron(p.cron)}
                className={`px-2 py-1 text-[10px] rounded-full border ${cron === p.cron ? "bg-blue-100 border-blue-300 text-blue-800 font-bold" : "bg-white border-gray-200 text-gray-600"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] uppercase font-bold text-gray-600">Cron (m h dom mon dow) — blank for one-time</label>
          <input value={cron} onChange={e => setCron(e.target.value)} placeholder="0 9 * * 1"
            data-testid="mk-schedule-cron"
            className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs font-mono" />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-600">Timezone</label>
            <input value={tz} onChange={e => setTz(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-600">Max runs</label>
            <input type="number" value={runs} onChange={e => setRuns(e.target.value)} placeholder="∞"
              data-testid="mk-schedule-runs"
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-gray-600">Starts at</label>
            <input type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)}
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold border border-gray-200 rounded">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="mk-schedule-save"
            className="px-3 py-1.5 bg-[#1a3c5e] text-white text-xs font-bold rounded inline-flex items-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />} Save schedule
          </button>
        </div>
      </div>
    </div>
  );
}
